import { Fragment, type ReactNode, useEffect, useState } from "react";
import { type Ai, CHATGPT, CLAUDE, connectPrompt, GROK, OTHER, parts } from "../../shared/connect.ts";
import type { Me } from "./api.ts";
import { ReportThis } from "./ReportThis.tsx";
import { Button, Icon, SegmentedControl } from "./souvenir.ts";
import { CopyLink, Prompt } from "./ui.tsx";

// Connecting an AI (features/getting-started.md): the prompt that asks an AI to set itself up, the screen
// that offers it, and each AI's own steps (also on the profile, for connecting another). The steps and the
// prompt are shared/connect.ts's, which tripmo.me/connect shows too.

type Pick = "claude" | "chatgpt" | "grok" | "other";
const PICKS: { value: Pick; label: string }[] = [
  { value: "claude", label: "Claude" },
  { value: "chatgpt", label: "ChatGPT" },
  { value: "grok", label: "Grok" },
  { value: "other", label: "Other" },
];
// The AI picked last on this device, so coming back shows the same steps.
const PICKED = "tripmo:connect-ai";

// Each AI's main way in: its website's settings (the public pages have its other ways too).
const GUIDES: Record<Pick, Ai> = { claude: CLAUDE, chatgpt: CHATGPT, grok: GROK, other: OTHER };

// A step's words, its marks drawn: a button's name bold, a link to the AI's own page in a new tab, the
// person's email bold and free to break anywhere.
function Words({ text, you }: { text: string; you: string }) {
  return parts(text).map((part, i) => {
    // A step's words are fixed, in order: their place is their key.
    const key = i;
    switch (part.kind) {
      case "bold":
        return <b key={key}>{part.text}</b>;
      case "code":
        return <code key={key}>{part.text}</code>;
      case "link":
        return (
          <Out key={key} href={part.href}>
            {part.text}
          </Out>
        );
      case "you":
        return (
          <b key={key} className="tm-anywhere">
            {you}
          </b>
        );
      default:
        return <Fragment key={key}>{part.text}</Fragment>;
    }
  });
}

// A link to another site, in a new tab.
function Out({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a className="tm-link tm-out" href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

function savedPick(): Pick {
  try {
    const saved = localStorage.getItem(PICKED);
    return PICKS.find((p) => p.value === saved)?.value ?? "claude";
  } catch {
    return "claude";
  }
}

// Pick your AI, and its steps. Also on the profile, for connecting another.
export function ConnectSteps({ url, you }: { url: string; you: string }) {
  const [pick, setPick] = useState<Pick>(savedPick);
  const guide = GUIDES[pick];
  const way = guide.ways[0];
  return (
    <div className="tm-ai-steps">
      <SegmentedControl
        label="Your AI"
        options={PICKS}
        value={pick}
        onChange={(v) => {
          setPick(v as Pick);
          try {
            localStorage.setItem(PICKED, v);
          } catch {}
        }}
      />
      <ol className="tm-steps" aria-label={`Connecting ${guide.name}`}>
        {way?.steps.map((step, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: a guide's steps are fixed, in order
          <li key={`${pick}-${i}`} className="tm-step">
            <span className="tm-step-n" aria-hidden="true">
              {i + 1}
            </span>
            <div className="tm-step-body">
              <p className="callout">
                <Words text={step.text} you={you} />
              </p>
              {step.note && <p className="caption tm-muted">{step.note}</p>}
              {step.address && <CopyLink url={url} />}
            </div>
          </li>
        ))}
      </ol>
      {way?.note && <p className="caption tm-muted">{way.note}</p>}
    </div>
  );
}

// Connect your AI: one way first, asking your AI to set it up (a prompt to copy), then a wait that
// hears the moment it's connected (getting started's own, GettingStarted.tsx), with each AI's own steps
// folded away under it. From a join link, `joining` names the trip and its owner, so it speaks of them.
// Sign-in, the page where an AI is allowed and friends' pads are server pages, so nothing here can stand
// in their way. `skip`: Skip for now, quiet, under the one obvious action, for someone who only wants to
// see a trip (features/getting-started.md, "Skip for now"); not offered when connecting again from the
// profile, which Your profile closes.
export function ConnectScreen({
  me,
  joining,
  check,
  onCheck,
  onHow,
  skip,
}: {
  me: Me;
  joining?: { title: string; owner: string };
  // "Check again": busy, and what it found when nothing came ("not_yet", or what went wrong).
  check: { busy: boolean; result: "not_yet" | string | null };
  onCheck: () => void;
  onHow: () => void;
  skip?: { busy: boolean; run: () => void };
}) {
  useEffect(() => {
    const before = document.title;
    document.title = "Connect your AI · Tripmo";
    return () => {
      document.title = before;
    };
  }, []);
  return (
    <main className="tm-connect-ai">
      {joining && <span className="eyebrow tm-sun-text">{joining.title}</span>}
      <h1 className="display-l">Connect your AI</h1>
      <p className="body tm-muted">
        {joining
          ? `You've asked to join. While ${joining.owner} decides, connect your AI so it's ready to plan ${joining.title} with you.`
          : "The easiest way: ask your AI to set it up."}
      </p>
      <Prompt text={connectPrompt(me.mcp_url)} primary label="What to send your AI" />
      <p className="body">
        Paste this into your AI. When it asks you to sign in to Tripmo and allow it, say yes, then come back here.
      </p>
      <section className="tm-wait" aria-live="polite" aria-label="Your AI">
        <span className="tm-wait-dot" aria-hidden="true" />
        <div className="tm-wait-text">
          <p className="title">Waiting for your AI to connect…</p>
          {check.result === "not_yet" ? (
            <p className="callout tm-muted">Not yet. Once you choose Allow on Tripmo's page, this changes by itself.</p>
          ) : check.result ? (
            <p className="callout tm-error" role="alert">
              {check.result}
              <ReportThis />
            </p>
          ) : (
            <p className="callout tm-muted">
              This changes by itself once it's connected, here and on your other devices.
            </p>
          )}
        </div>
        <Button variant="quiet" size="sm" disabled={check.busy} onClick={onCheck}>
          Check again
        </Button>
      </section>
      {skip && (
        <div className="tm-skip">
          <Button variant="quiet" size="sm" className="tm-start" disabled={skip.busy} onClick={skip.run}>
            Skip for now
          </Button>
          <p className="caption tm-muted">
            {joining
              ? "You can see the trip, rate, tick and add notes without an AI. Connect it any time from your profile."
              : "You can see your trips, rate, tick and add notes without an AI. Connect it any time from your profile."}
          </p>
        </div>
      )}
      <details className="tm-fold">
        <summary className="tm-fold-head">
          <span className="body-strong">Or follow the steps for your AI</span>
          <Icon name="chevron" size={20} className="tm-fold-chevron" />
        </summary>
        <div className="tm-fold-body">
          <ConnectSteps url={me.mcp_url} you={me.user.email} />
        </div>
      </details>
      <Button variant="quiet" size="sm" className="tm-start" onClick={onHow}>
        How Tripmo works
      </Button>
    </main>
  );
}
